<script setup lang="ts">
type Props = {
  modelValue?: number
  min?: number
  max?: number
  size?: 'md' | 'lg'
  fullWidth?: boolean
  class?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: 0,
  min: 0,
  max: 99,
  size: 'md',
  fullWidth: false,
  class: undefined,
})

const emit = defineEmits<{
  (event: 'update:modelValue', value: number): void
  (event: 'blur'): void
}>()

const classes = computed(() => ({
  'app-input-quantity': true,
  [`app-input-quantity--size-${props.size}`]: true,
  'app-input-quantity--full-width': props.fullWidth,
  [`${props.class}`]: !!props.class,
}))

const onChange = (event: Event) => {
  const target = event.target as HTMLInputElement
  const value = parseInt(target.value, 10)

  if (isNaN(value)) {
    emit('update:modelValue', Math.min(Math.max(0, props.min), props.max))
  } else {
    emit('update:modelValue', Math.min(Math.max(value, props.min), props.max))
  }
}

const decrease = () =>
  emit(
    'update:modelValue',
    Math.min(Math.max(props.modelValue - 1, props.min), props.max),
  )
const increase = () =>
  emit(
    'update:modelValue',
    Math.min(Math.max(props.modelValue + 1, props.min), props.max),
  )

onMounted(() => {
  const limitedValue = Math.min(
    Math.max(props.modelValue, props.min),
    props.max,
  )

  if (props.modelValue !== limitedValue) {
    emit('update:modelValue', limitedValue)
  }
})

onUpdated(() => {
  const limitedValue = Math.min(
    Math.max(props.modelValue, props.min),
    props.max,
  )

  if (props.modelValue !== limitedValue) {
    emit('update:modelValue', limitedValue)
  }
})
</script>

<template>
  <div :class="classes">
    <button type="button" aria-label="decrease" @click="decrease">
      <IconMinus />
    </button>

    <input
      type="text"
      :value="modelValue"
      @input="onChange"
      @blur="$emit('blur')"
    />

    <button type="button" aria-label="increase" @click="increase">
      <IconPlus />
    </button>
  </div>
</template>

<style lang="scss">
@import '@boats/ui/styles/config';

.app-input-quantity {
  position: relative;
  display: flex;

  input {
    position: relative;
    display: block;
    width: 2.75rem;
    height: 2.75rem;
    margin: 0 -1px;
    padding: 0;
    font-family: $font-barlow;
    font-size: 0.875rem;
    line-height: 1.42857143;
    text-align: center;
    color: $black;
    background-color: $gray-100;
    border: 1px solid $black;
    z-index: 1;
  }

  button {
    flex-shrink: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 2.75rem;
    height: 2.75rem;
    margin: 0;
    padding: 0;
    color: $black;
    border: 1px solid $black;
    @include transition('color, background-color, border-color');

    svg {
      width: 1rem;
      height: 1rem;
    }

    &:first-child {
      border-radius: 0.125rem 0 0 0.125rem;
    }

    &:last-child {
      border-radius: 0 0.125rem 0.125rem 0;
    }

    &:hover {
      background-color: $black;
      color: $white;
    }
  }
}

.app-input-quantity--size-lg {
  button {
    width: 4.75rem;
    height: 3.375rem;
    color: $white;
    background-color: $black;

    &:hover {
      background-color: $gray-400;
    }
  }

  input {
    height: 3.375rem;
  }

  @include media-breakpoint-up(md) {
    button {
      width: 7.75rem;
      height: 3.875rem;
    }

    input {
      height: 3.875rem;
    }
  }
}

.app-input-quantity--full-width {
  width: 100%;

  input {
    flex: 1 1 0%;
    width: 100%;
  }
}
</style>
